import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, X, Clock, TrendingUp, ArrowRight } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { router } from "@inertiajs/react";
import Fuse from "fuse.js";
import { News } from "@/types";

// Use the existing News interface instead of custom SearchResult
type SearchResult = News;

interface UniversalSearchProps {
  placeholder?: string;
  onResultClick?: (result: SearchResult) => void;
  className?: string;
  btnClassName?: string;
  iconSize?: number;
  searchData?: SearchResult[];
  compact?: boolean; // New prop for compact mode
  onExpandChange?: (isExpanded: boolean) => void; // Callback for expansion state
}

const UniversalSearch: React.FC<UniversalSearchProps> = ({
  placeholder = "Cari berita, kategori, atau penulis...",
  onResultClick,
  className = "",
  btnClassName = "",
  iconSize = 4,
  searchData = [],
  compact = false,
  onExpandChange,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!compact); // New state for expansion
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [allNews, setAllNews] = useState<SearchResult[]>([]);

  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Notify parent component about expansion state changes
  useEffect(() => {
    if (onExpandChange && compact) {
      onExpandChange(isExpanded);
    }
  }, [isExpanded, onExpandChange, compact]);

  const debouncedQuery = useDebounce(query, 300);

  // Fuse.js configuration
  const fuseOptions = {
    keys: [
      { name: "title", weight: 0.7 },
      { name: "content", weight: 0.3 },
      { name: "author.name", weight: 0.5 },
      { name: "author.username", weight: 0.4 },
      { name: "category.name", weight: 0.4 },
    ],
    threshold: 0.3,
    includeScore: true,
    includeMatches: true,
    minMatchCharLength: 2,
  };

  const fuse = React.useMemo(() => new Fuse(allNews, fuseOptions), [allNews]);

  // Load initial search data
  useEffect(() => {
    const loadSearchData = async () => {
      // Check if we have fresh cached data (less than 10 minutes old)
      const cachedData = localStorage.getItem("searchData");
      const cachedTimestamp = localStorage.getItem("searchDataTimestamp");
      const cacheAge = cachedTimestamp
        ? Date.now() - parseInt(cachedTimestamp)
        : Infinity;
      const cacheMaxAge = 10 * 60 * 1000; // 10 minutes

      if (cachedData && cacheAge < cacheMaxAge) {
        try {
          const parsed = JSON.parse(cachedData);
          setAllNews(parsed);
          return; // Use cached data
        } catch (e) {
          console.error("Failed to parse cached search data:", e);
        }
      }

      // Load fresh data from API
      try {
        const response = await fetch("/api/news?per_page=50");

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Check for correct API response format
        if (data.status === true && data.data) {
          setAllNews(data.data);
          // Cache the fresh data
          localStorage.setItem("searchData", JSON.stringify(data.data));
          localStorage.setItem("searchDataTimestamp", Date.now().toString());
        } else {
          console.error("API response format error:", {
            status: data.status,
            hasData: !!data.data,
            message: data.message,
          });
          // Try to use fallback data if available
          if (cachedData) {
            try {
              const parsed = JSON.parse(cachedData);
              setAllNews(parsed);
            } catch (e) {
              console.error("Failed to parse cached search data:", e);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load search data:", error);
        // Fallback: try to load from cache even if old
        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            setAllNews(parsed);
          } catch (e) {
            console.error("Failed to parse cached search data:", e);
          }
        }
      }
    };

    if (allNews.length === 0) {
      loadSearchData();
    }
  }, []);

  // Perform search using Fuse.js
  const performSearch = useCallback(
    (searchQuery: string) => {
      if (!searchQuery.trim() || allNews.length === 0) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const fuseResults = fuse.search(searchQuery);

        const searchResults = fuseResults
          .slice(0, 8)
          .map((result) => result.item);

        setResults(searchResults);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [allNews],
  );

  // Effect for debounced search
  useEffect(() => {
    if (debouncedQuery) {
      performSearch(debouncedQuery);
    } else {
      setResults([]);
    }
  }, [debouncedQuery, performSearch]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);
  };

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    // Save to recent searches
    const newRecentSearches = [
      query,
      ...recentSearches.filter((s) => s !== query),
    ].slice(0, 5);
    setRecentSearches(newRecentSearches);
    localStorage.setItem("recentSearches", JSON.stringify(newRecentSearches));

    // Navigate to result with fallback
    try {
      router.visit(`/news/${result.slug || result.id}`);
    } catch (error) {
      console.warn("Inertia navigation failed, using window.location:", error);
      // Fallback to regular navigation
      window.location.href = `/news/${result.slug || result.id}`;
    }
    setIsOpen(false);
    setQuery("");

    onResultClick?.(result);
  };

  // Handle trending search click
  const handleTrendingClick = (term: string) => {
    setQuery(term);
    inputRef.current?.focus();
  };

  // Handle "View All Results" click - show more results in dropdown
  const handleViewAllResults = () => {
    if (query.trim() && allNews.length > 0) {
      const fuseResults = fuse.search(query);
      const allResults = fuseResults
        .slice(0, 20) // Show up to 20 results
        .map((result) => result.item);

      setResults(allResults);
    }
  };

  // Load recent searches on mount
  useEffect(() => {
    const saved = localStorage.getItem("recentSearches");
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (error) {
        console.error("Failed to parse recent searches:", error);
      }
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Collapse in compact mode if no query
        if (compact && !query) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [compact, query]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
      // Collapse in compact mode
      if (compact && !query) {
        setIsExpanded(false);
      }
    } else if (e.key === "Enter" && query.trim()) {
      handleViewAllResults();
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Truncate text
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Handle expand/collapse for compact mode
  const handleExpand = () => {
    setIsExpanded(true);
    setIsOpen(true);
    // Focus input after expansion animation
    setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
  };

  const handleCollapse = () => {
    if (compact && !query && !isOpen) {
      setIsExpanded(false);
    }
  };

  // Handle input focus for compact mode
  const handleInputFocus = () => {
    setIsOpen(true);
    if (compact) {
      setIsExpanded(true);
    }
  };

  // Handle input blur for compact mode
  const handleInputBlur = () => {
    // Delay collapse to allow for dropdown interactions
    setTimeout(() => {
      if (compact && !query && !isOpen) {
        setIsExpanded(false);
      }
    }, 200);
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative w-full flex justify-end px-3 md:px-0">
        {/* Compact Mode: Icon Only */}
        {compact && !isExpanded && (
          <button
            onClick={handleExpand}
            className={`${btnClassName} flex items-center justify-center  hover:scale-105 focus:ring-2 focus:ring-white/30 focus:border-white/40 text-white transition-all duration-200 group active:scale-95`}
            aria-label="Search"
          >
            <Search
              className={`w-${iconSize} h-${iconSize} text-white/70 group-hover:text-white transition-colors duration-200`}
            />
          </button>
        )}

        {/* Expanded Mode: Full Input */}
        {(!compact || isExpanded) && (
          <>
            <Search
              className={`absolute left-[22px] md:left-3 top-1/2 transform -translate-y-1/2 text-white/70 w-${iconSize} h-${iconSize} z-10`}
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={`w-full md:max-w-md lg:max-w-lg xl:max-w-xl px-10 py-3 md:py-2 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-white/30 focus:border-white/40 text-white placeholder-white/70 text-sm backdrop-blur-sm transition-all duration-300 ${
                compact
                  ? "animate-in slide-in-from-right-2 duration-300 ease-out"
                  : ""
              }`}
            />
            {query && (
              <button
                onClick={() => {
                  setQuery("");
                  setResults([]);
                  inputRef.current?.focus();
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {/* Collapse button for compact mode */}
            {compact && isExpanded && !query && (
              <button
                onClick={() => {
                  setIsExpanded(false);
                  setIsOpen(false);
                }}
                className="absolute right-5 md:right-3 p-1 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10"
                aria-label="Collapse search"
              >
                <X className="w-4 h-4 stroke-3" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (compact ? isExpanded : true) && (
        <div
          className={`mt-3 absolute left-0 w-[calc(100vw)] md:left-auto md:right-auto md:w-[400px] lg:w-[500px] md:max-w-none max-h-[70vh] md:max-h-[500px] md:top-full md:mt-2 bg-white/50 dark:bg-gray-800/80 backdrop-blur-lg border border-gray-200/40 dark:border-gray-700 md:rounded-lg shadow-xl z-50 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent ${
            compact ? "" : ""
          }`}
        >
          {/* Loading State */}
          {isLoading && (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Mencari...</p>
            </div>
          )}

          {/* Search Results */}
          {!isLoading && results.length > 0 && (
            <div className="py-2">
              <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                Hasil Pencarian
              </div>
              {results.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleResultClick(result)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700 focus:outline-none border-b border-gray-50 dark:border-gray-700 last:border-b-0 transition-colors"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 mb-1">
                        {truncateText(result.title, 100)}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 mb-2">
                        {truncateText(
                          result.content?.replace(/<[^>]*>/g, "") || "",
                          120,
                        )}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full">
                          {result.category.name}
                        </span>
                        {/* <span>•</span> */}
                        <span>{result.views} views</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}

              {/* Show More Results */}
              {query && results.length >= 8 && (
                <button
                  onClick={handleViewAllResults}
                  className="w-full px-3 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:outline-none border-t border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between text-blue-600 dark:text-blue-400">
                    <span className="text-sm font-medium">
                      Tampilkan lebih banyak hasil untuk "{query}"
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              )}
            </div>
          )}

          {/* No Results */}
          {!isLoading &&
            query &&
            results.length === 0 &&
            allNews.length > 0 && (
              <div className="p-4 text-center">
                <p className="text-sm text-gray-500">
                  Tidak ada hasil untuk "{query}"
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Coba kata kunci yang berbeda
                </p>
              </div>
            )}

          {/* Recent & Trending Searches */}
          {!query && (
            <div className="py-2">
              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div className="mb-4">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center border-b border-gray-100 dark:border-gray-700">
                    <Clock className="w-3 h-3 mr-1" />
                    Pencarian Terakhir
                  </div>
                  {recentSearches.map((term, index) => (
                    <button
                      key={index}
                      onClick={() => handleTrendingClick(term)}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UniversalSearch;
